import { useState, useEffect, useRef } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import {
  LayoutDashboard, Receipt, TrendingUp, Landmark, PiggyBank,
  FolderTree, BarChart3, Bot, MessageCircle, Users, ScrollText,
  Settings, LogOut, Shield, ChevronDown, Plus, Building2, Check, Store, BookOpen, ArrowLeftRight, Link2, Activity, Compass, FolderOpen, MapPin, CreditCard, Sparkles, Send
} from 'lucide-react';
import { ScrollArea } from '../ui/scroll-area';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Button } from '../ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { toast } from 'sonner';

const navItems = [
  { to: '/', icon: LayoutDashboard, label: 'Dashboard' },
  { to: '/expenses', icon: Receipt, label: 'Expenses' },
  { to: '/revenue', icon: TrendingUp, label: 'Revenue' },
  { to: '/vendors', icon: Store, label: 'Vendors & Customers', notDeptHead: true },
  { to: '/chart-of-accounts', icon: BookOpen, label: 'Chart of Accounts', notDeptHead: true },
  { to: '/banking', icon: Landmark, label: 'Banking', notDeptHead: true },
  { to: '/cards', icon: CreditCard, label: 'Cards', adminOnly: true },
  { to: '/transactions', icon: ArrowLeftRight, label: 'Receipts & Payments', notDeptHead: true },
  { to: '/payment-requests', icon: Send, label: 'Payment Requests' },
  { to: '/reconciliation', icon: Link2, label: 'Reconciliation', notDeptHead: true },
  { to: '/budgets', icon: PiggyBank, label: 'Budgets' },
  { to: '/cost-centers', icon: FolderTree, label: 'Cost Centers', notDeptHead: true },
  { to: '/reports', icon: BarChart3, label: 'Reports' },
  { divider: true, label: 'Operations' },
  { to: '/team', icon: Users, label: 'Team Management', adminOnly: true, deptHeadOk: true, ownerOk: true },
  { to: '/assets', icon: Compass, label: 'Asset Command', adminOnly: true, deptHeadOk: true, ownerOk: true },
  { to: '/asset-folders', icon: FolderOpen, label: 'Asset Folders', adminOnly: true, deptHeadOk: true, ownerOk: true },
  { to: '/map', icon: MapPin, label: 'Fleet Map', adminOnly: true, deptHeadOk: true, ownerOk: true },
  { divider: true, label: 'Intelligence' },
  { to: '/ai-assistant', icon: Bot, label: 'AI Assistant' },
  { to: '/ai-tools', icon: Sparkles, label: 'AI Tools' },
  { to: '/intelligence', icon: Activity, label: 'Analytics' },
  { to: '/messaging', icon: MessageCircle, label: 'Messaging', hidden: true },
  { divider: true, label: 'Administration' },
  { to: '/users', icon: Users, label: 'Users & Permissions', adminOnly: true },
  { to: '/audit-log', icon: ScrollText, label: 'Audit Log', adminOnly: true },
  { to: '/settings', icon: Settings, label: 'Settings', adminOnly: true },
  { divider: true, label: 'Platform', superAdminDivider: true },
  { to: '/platform', icon: Shield, label: 'Platform Admin', superAdminOnly: true },
];

export default function Sidebar({ onNavigate }) {
  const { user, clients, activeClient, logout, switchClient, addClient } = useAuth();
  const location = useLocation();
  const role = activeClient?.role || 'view_only';
  const isSuperAdmin = user?.is_super_admin;
  const [showClientDropdown, setShowClientDropdown] = useState(false);
  const [showNewClientDialog, setShowNewClientDialog] = useState(false);
  const [newClientForm, setNewClientForm] = useState({ name: '', code: '', reporting_currency: 'EUR' });
  const [creating, setCreating] = useState(false);
  const dropdownRef = useRef(null);

  // Close dropdown on outside click
  useEffect(() => {
    const handleClick = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setShowClientDropdown(false);
      }
    };
    if (showClientDropdown) document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [showClientDropdown]);

  const handleSwitchClient = (client) => {
    switchClient(client);
    setShowClientDropdown(false);
    toast.success(`Switched to ${client.name}`);
  };

  const handleCreateClient = async (e) => {
    e.preventDefault();
    if (!newClientForm.name || !newClientForm.code) {
      toast.error('Name and code are required');
      return;
    }
    setCreating(true);
    try {
      const newClient = await addClient(newClientForm);
      toast.success(`Client "${newClient.name}" created`);
      setShowNewClientDialog(false);
      setNewClientForm({ name: '', code: '', reporting_currency: 'EUR' });
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error creating client');
    }
    setCreating(false);
  };

  return (
    <aside className="fixed left-0 top-0 h-screen w-72 flex flex-col z-50" style={{ background: 'linear-gradient(180deg, #0f172a 0%, #1e293b 100%)' }}>
      {/* Logo */}
      <div className="p-6 border-b border-white/10">
        <div className="flex flex-col gap-0.5">
          <h1 className="text-2xl font-black leading-none" style={{ fontFamily: 'Manrope, sans-serif', color: '#d4a853', letterSpacing: '0.08em' }}>VALTOR</h1>
          <p className="text-[9px] uppercase" style={{ color: 'rgba(148,163,184,0.5)', letterSpacing: '0.25em' }}>Private Office</p>
        </div>
      </div>

      {/* Client selector - now interactive */}
      <div className="relative border-b border-white/10" ref={dropdownRef}>
        <button
          onClick={() => setShowClientDropdown(!showClientDropdown)}
          className="w-full px-4 py-3 text-left hover:bg-white/5 transition-colors"
          data-testid="client-switcher-btn"
        >
          <p className="text-[10px] text-slate-500 uppercase tracking-widest mb-1">Active Client</p>
          <div className="flex items-center justify-between">
            <div className="min-w-0 flex-1">
              <p className="text-sm text-white font-medium truncate" data-testid="active-client-name">
                {activeClient?.name || 'Select a client'}
              </p>
              <span className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded mt-1 inline-block" style={{ background: 'rgba(212, 168, 83, 0.15)', color: '#d4a853' }}>{role}</span>
            </div>
            <ChevronDown className={`w-4 h-4 text-slate-400 transition-transform ${showClientDropdown ? 'rotate-180' : ''}`} />
          </div>
        </button>

        {/* Client Dropdown */}
        {showClientDropdown && (
          <div className="absolute left-0 right-0 top-full z-50 bg-[#1e293b] border-b border-white/10 shadow-xl max-h-64 overflow-y-auto">
            {clients.map(client => (
              <button
                key={client.id}
                onClick={() => handleSwitchClient(client)}
                className={`w-full px-4 py-2.5 text-left flex items-center gap-3 transition-colors ${
                  client.id === activeClient?.id ? 'bg-white/10' : 'hover:bg-white/5'
                }`}
                data-testid={`switch-client-${client.id}`}
              >
                <div className="w-7 h-7 rounded-md bg-slate-700 flex items-center justify-center text-[10px] text-white font-bold shrink-0">
                  {client.code?.substring(0, 2) || client.name?.charAt(0)}
                </div>
                <div className="min-w-0 flex-1">
                  <p className="text-sm text-white truncate">{client.name}</p>
                  <p className="text-[10px] text-slate-500">{client.code} &middot; {client.role}</p>
                </div>
                {client.id === activeClient?.id && (
                  <Check className="w-4 h-4 shrink-0" style={{ color: '#d4a853' }} />
                )}
              </button>
            ))}
            {/* New Client button - only for super admin */}
            {isSuperAdmin && (
              <button
                onClick={() => { setShowClientDropdown(false); setShowNewClientDialog(true); }}
                className="w-full px-4 py-2.5 text-left flex items-center gap-3 hover:bg-white/5 transition-colors border-t border-white/10"
                data-testid="new-client-btn"
              >
                <div className="w-7 h-7 rounded-md flex items-center justify-center shrink-0" style={{ background: 'rgba(212, 168, 83, 0.2)' }}>
                  <Plus className="w-3.5 h-3.5" style={{ color: '#d4a853' }} />
                </div>
                <p className="text-sm font-medium" style={{ color: '#d4a853' }}>New Client</p>
              </button>
            )}
          </div>
        )}
      </div>

      {/* Navigation */}
      <ScrollArea className="flex-1 py-2">
        <nav className="px-3 space-y-0.5">
          {navItems.map((item, i) => {
            if (item.divider) {
              if (item.superAdminDivider && !isSuperAdmin) return null;
              // Hide ADMINISTRATION divider for view_only and owner (no items under it)
              if (!item.superAdminDivider && item.label === 'Administration' && ['view_only', 'owner', 'dept_head'].includes(role)) return null;
              return <p key={i} className={`text-[10px] uppercase tracking-widest px-3 pt-5 pb-2 ${item.superAdminDivider ? 'text-amber-500/70' : 'text-slate-500'}`}>{item.label}</p>;
            }
            if (item.hidden) return null;
            if (item.adminOnly) {
              const isOwner = role === 'owner';
              const isAdmin = role === 'admin' || role === 'super_admin';
              const isDeptHeadAllowed = role === 'dept_head' && item.deptHeadOk;
              const isOwnerAllowed = isOwner && item.ownerOk;
              // Block if not admin AND not specifically allowed for this role
              if (!isAdmin && !isDeptHeadAllowed && !isOwnerAllowed) return null;
            }
            if (item.superAdminOnly && !isSuperAdmin) return null;
            // Hide items not allowed for dept_head
            if (item.notDeptHead && role === 'dept_head') return null;
            const isActive = item.to === '/' ? location.pathname === '/' : location.pathname.startsWith(item.to);
            return (
              <NavLink
                key={item.to}
                to={item.to}
                onClick={onNavigate}
                data-testid={`nav-${item.label.toLowerCase().replace(/\s/g, '-')}`}
                className={`sidebar-item flex items-center gap-3 px-3 py-2.5 rounded-md text-sm font-medium transition-all ${isActive ? 'sidebar-item-active text-white' : 'text-slate-400 hover:text-slate-200'}`}
              >
                <item.icon className="w-[18px] h-[18px] shrink-0" strokeWidth={1.5} />
                <span>{item.label}</span>
              </NavLink>
            );
          })}
        </nav>
      </ScrollArea>

      {/* User section */}
      <div className="p-4 border-t border-white/10">
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 rounded-full bg-slate-700 flex items-center justify-center text-xs text-white font-semibold">
            {user?.name?.charAt(0) || 'U'}
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm text-white truncate">{user?.name}</p>
            <p className="text-[11px] text-slate-500 truncate">{user?.email}</p>
          </div>
          <button onClick={logout} className="text-slate-500 hover:text-white transition-colors" data-testid="logout-btn">
            <LogOut className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* New Client Dialog */}
      <Dialog open={showNewClientDialog} onOpenChange={setShowNewClientDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Manrope' }}>Create New Client</DialogTitle>
            <DialogDescription>Set up a new Family Office or entity. Default departments and chart of accounts will be created automatically.</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleCreateClient} className="space-y-4">
            <div>
              <Label>Client Name</Label>
              <Input
                value={newClientForm.name}
                onChange={e => setNewClientForm({...newClientForm, name: e.target.value})}
                placeholder="e.g. Sterling Family Office"
                required
                data-testid="new-client-name-input"
              />
            </div>
            <div>
              <Label>Code (short identifier)</Label>
              <Input
                value={newClientForm.code}
                onChange={e => setNewClientForm({...newClientForm, code: e.target.value.toUpperCase()})}
                placeholder="e.g. SFO"
                maxLength={10}
                required
                data-testid="new-client-code-input"
              />
            </div>
            <div>
              <Label>Reporting Currency</Label>
              <Select value={newClientForm.reporting_currency} onValueChange={v => setNewClientForm({...newClientForm, reporting_currency: v})}>
                <SelectTrigger data-testid="new-client-currency-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="EUR">EUR - Euro</SelectItem>
                  <SelectItem value="GBP">GBP - British Pound</SelectItem>
                  <SelectItem value="USD">USD - US Dollar</SelectItem>
                  <SelectItem value="CHF">CHF - Swiss Franc</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="flex justify-end gap-3 pt-2">
              <Button type="button" variant="outline" onClick={() => setShowNewClientDialog(false)}>Cancel</Button>
              <Button type="submit" disabled={creating} style={{ background: '#d4a853', color: '#0f172a' }} className="font-semibold" data-testid="create-client-submit-btn">
                {creating ? 'Creating...' : 'Create Client'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </aside>
  );
}
